import { useMemo } from 'react'
import { positionToDomain } from '@editorUtils'
import { find } from 'lodash-es'
import { useAppStore } from '@stores'
import AreaLoadMeshElement from '../AreaLoadMeshElement'
import LineLoadMeshElement from '../LineLoadMeshElement'
import PointLoadMeshElement from '../PointLoadMeshElement'
import { createLineLoadDisplayData, createPointLoadDisplayData } from './utils'

interface Props {
  loads: ElementLoad[]
  domains: Domain[]
  positions: Position[]
  elementTargetDomains: Record<string, Domain | null>
  onClick?: (load: ElementLoad) => void
  activeLoadGuid?: string
  hiddenElements?: Set<string>
}

const LoadMesh = ({
  loads,
  domains,
  positions,
  elementTargetDomains,
  onClick = () => null,
  activeLoadGuid,
  hiddenElements = new Set(),
}: Props) => {
  const loadDisplayScale = useAppStore(state => state.loadDisplayScale)
  const lineLoadRenderConfig = useAppStore(state => state.loadRenderConfig.lineLoads)
  const pointLoadRenderConfig = useAppStore(state => state.loadRenderConfig.pointLoads)

  const lineLoadLabelFormat = useMemo(
    () => ({
      showUnitsInScene: lineLoadRenderConfig.showUnitsInScene,
      showUnitInNewLine: lineLoadRenderConfig.showUnitInNewLine,
    }),
    [lineLoadRenderConfig.showUnitsInScene, lineLoadRenderConfig.showUnitInNewLine],
  )

  const pointLoadLabelFormat = useMemo(
    () => ({
      showUnitsInScene: pointLoadRenderConfig.showUnitsInScene,
      showUnitInNewLine: pointLoadRenderConfig.showUnitInNewLine,
    }),
    [pointLoadRenderConfig.showUnitsInScene, pointLoadRenderConfig.showUnitInNewLine],
  )

  // compute load attribute that is to be diplayed from loads
  const loadsDisplayData = useMemo(() => {
    const visibleLoads = loads.filter(({ element_guid }) => !hiddenElements.has(element_guid))

    return visibleLoads.map(load => {
      const { domain_guid, element_guid, load_type } = load

      if (load_type === 'line-load') {
        const position = find(positions, { position_guid: element_guid })

        const domain =
          // @ts-ignore
          (load.domain as Domain) ||
          elementTargetDomains[domain_guid] ||
          (find(domains, ['guid', domain_guid]) as Domain) ||
          positionToDomain(position as Position)

        return {
          load,
          data: createLineLoadDisplayData({
            load,
            domainStart: domain.start,
            domainEnd: domain.end,
            scale: loadDisplayScale,
            domain,
            labelFormat: lineLoadLabelFormat,
          }),
        }
      } else if (load_type === 'point-load') {
        const position = find(positions, { position_guid: element_guid })

        const domain =
          // @ts-ignore
          (load.domain as Domain) ||
          elementTargetDomains[domain_guid] ||
          (find(domains, ['guid', domain_guid]) as Domain) ||
          positionToDomain(position as Position)

        return {
          load,
          data: createPointLoadDisplayData({
            load,
            domainStart: domain.start,
            domainEnd: domain.end,
            scale: loadDisplayScale,
            domain,
            labelFormat: pointLoadLabelFormat,
          }),
        }
      } else {
        return {
          load,
          data: {},
        }
      }
    })
  }, [
    loads,
    hiddenElements,
    positions,
    elementTargetDomains,
    domains,
    loadDisplayScale,
    lineLoadLabelFormat,
    pointLoadLabelFormat,
  ])

  return (
    <>
      {loadsDisplayData.map(({ load, data }) => {
        if (load.load_type === 'line-load') {
          return (
            <LineLoadMeshElement
              load={load}
              key={load.guid}
              onClick={() => onClick(load)}
              loadDisplayData={data as LineLoadDisplayData}
              isActive={load.guid === activeLoadGuid}
            />
          )
        }

        if (load.load_type === 'point-load') {
          return (
            <PointLoadMeshElement
              load={load}
              key={load.guid}
              onClick={() => onClick(load)}
              loadDisplayData={data as PointLoadDisplayData}
              isActive={load.guid === activeLoadGuid}
            />
          )
        }

        if (load.load_type === 'area-load') {
          return (
            <AreaLoadMeshElement
              key={load.guid}
              onClick={() => onClick(load)}
              load={load}
              isActive={load.guid === activeLoadGuid}
            />
          )
        }

        return null
      })}
    </>
  )
}

export default LoadMesh
